import React, { useState, useCallback, useMemo, useEffect } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "./LearningPlans.css";
import ValueSelector from "./components/ValueSelector";
import CompetencySelector from "./components/CompetencySelector";
import CompetencyTable from "./components/CompetencyTable";
import {
  useGetMyDevelopmentPlanQuery,
  useGetDevelopmentPlanValuesQuery,
  useCreateDevelopmentPlanMutation,
  useUpdateDevelopmentPlanMutation,
  useUpdateDevelopmentPlanEntryMutation,
  useSelectDevelopmentCompetenciesMutation,
  useAddDevelopmentEntryMutation,
  useRemoveDevelopmentEntryMutation,
} from "../../services/api";

export default function DevelopmentPlans() {
  const [pendingValue, setPendingValue] = useState("");
  const [expandedStep, setExpandedStep] = useState(1);

  // API hooks
  const { data: plan, isLoading, error } = useGetMyDevelopmentPlanQuery();
  const { data: valuesData, isLoading: isLoadingValues } = useGetDevelopmentPlanValuesQuery();
  const [createPlan, { isLoading: isCreating }] = useCreateDevelopmentPlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdateDevelopmentPlanMutation();
  const [updateEntry, { isLoading: isSavingEntry }] = useUpdateDevelopmentPlanEntryMutation();
  const [selectCompetencies, { isLoading: isSelectingCompetencies }] = useSelectDevelopmentCompetenciesMutation();
  const [addEntry] = useAddDevelopmentEntryMutation();
  const [removeEntry] = useRemoveDevelopmentEntryMutation();

  const values = valuesData?.values || [];

  const selectedValueData = useMemo(() => {
    if (!plan?.selectedValue || !values.length) return null;
    return values.find(v => v.name === plan.selectedValue);
  }, [plan?.selectedValue, values]);

  const selectedCompetencyIndexes = useMemo(() => {
    if (!plan?.selectedCompetenciesByValue || !plan?.selectedValue) return [];
    return plan.selectedCompetenciesByValue[plan.selectedValue] || [];
  }, [plan?.selectedCompetenciesByValue, plan?.selectedValue]);

  const entriesByCompetency = useMemo(() => {
    if (!plan?.competencyEntries) return {};
    return plan.competencyEntries.reduce((acc, entry, entryIndex) => {
      const key = entry.competencyIndex;
      if (!acc[key]) acc[key] = [];
      acc[key].push({ ...entry, entryIndex });
      return acc;
    }, {});
  }, [plan?.competencyEntries]);

  const recommendationsByCompetency = useMemo(() => {
    if (!selectedValueData?.competencies) return {};
    const map = {};
    selectedValueData.competencies.forEach((comp, idx) => {
      const key = comp.index ?? idx;
      if (comp.developmentGoals?.length > 0) {
        map[key] = comp.developmentGoals;
      }
    });
    return map;
  }, [selectedValueData]);

  const resourcesByCompetency = useMemo(() => {
    if (!selectedValueData?.competencies) return {};
    const map = {};
    selectedValueData.competencies.forEach((comp, idx) => {
      const key = comp.index ?? idx;
      if (comp.recommendedResources?.length > 0) {
        map[key] = comp.recommendedResources;
      }
    });
    return map;
  }, [selectedValueData]);

  // Auto-advance step based on completed state
  const step1Done = !!plan?.selectedValue;
  const step2Done = selectedCompetencyIndexes.length > 0;

  useEffect(() => {
    if (!plan) {
      setExpandedStep(1);
    } else if (step1Done && !step2Done) {
      setExpandedStep(2);
    }
    // Don't auto-advance to step 3 — let user finish selecting competencies first
  }, [plan, step1Done, step2Done]);

  const handleValueChange = async (newValue) => {
    if (!newValue) return;
    if (plan) {
      try {
        await updatePlan({ selectedValue: newValue }).unwrap();
        setExpandedStep(2);
      } catch (err) {
        console.error("Failed to update plan:", err);
      }
    } else {
      setPendingValue(newValue);
      try {
        await createPlan({ selectedValue: newValue }).unwrap();
        setPendingValue("");
        setExpandedStep(2);
      } catch (err) {
        console.error("Failed to create plan:", err);
        setPendingValue("");
      }
    }
  };

  const handleCompetencySelectionChange = async (indexes) => {
    try {
      await selectCompetencies({ competencyIndexes: indexes }).unwrap();
    } catch (err) {
      console.error("Failed to update competency selection:", err);
    }
  };

  const handleEntrySave = useCallback(async (entryIndex, data) => {
    try {
      await updateEntry({ competencyIndex: entryIndex, ...data }).unwrap();
    } catch (err) {
      console.error("Failed to save entry:", err);
      throw err;
    }
  }, [updateEntry]);

  const handleAddEntry = useCallback(async (competencyIndex, data = {}) => {
    try {
      await addEntry({ competencyIndex, ...data }).unwrap();
    } catch (err) {
      console.error("Failed to add entry:", err);
    }
  }, [addEntry]);

  const handleRemoveEntry = useCallback(async (entryIndex) => {
    try {
      await removeEntry({ entryIndex }).unwrap();
    } catch (err) {
      console.error("Failed to remove entry:", err);
    }
  }, [removeEntry]);

  const handleDownloadPdf = useCallback(() => {
    if (!plan || !selectedValueData) return;

    const doc = new jsPDF({ orientation: "landscape" });
    const valueName = plan.selectedValue;

    // Title
    doc.setFontSize(18);
    doc.setTextColor(25, 58, 49);
    doc.text("Development Plan", 14, 18);
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`Focus Area: ${valueName}`, 14, 26);

    let startY = 34;

    selectedCompetencyIndexes
      .slice()
      .sort((a, b) => a - b)
      .forEach((compIndex) => {
        const entries = entriesByCompetency[compIndex] || [];
        const compData = selectedValueData?.competencies?.find(c => (c.index ?? 0) === compIndex);
        const compName = compData?.name || entries[0]?.competencyName || `Competency ${compIndex + 1}`;

        if (entries.length === 0) return;

        // Competency heading
        doc.setFontSize(13);
        doc.setTextColor(25, 58, 49);
        doc.text(compName, 14, startY);
        startY += 4;

        const tableRows = entries.map((entry) => [
          entry.developmentPlanText || "",
          entry.onTheJobApplication || "",
          entry.mentoringPeerSupport || "",
          (entry.formalLearning || []).map(r => r.resourceTitle).join(", ") || "",
        ]);

        autoTable(doc, {
          startY,
          head: [[
            "Development Plan",
            "Experience-Based (70%)",
            "Coaching & Learning (20%)",
            "Formal Training (10%)",
          ]],
          body: tableRows,
          theme: "grid",
          headStyles: {
            fillColor: [25, 58, 49],
            fontSize: 9,
            cellPadding: 4,
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 4,
            valign: "top",
          },
          columnStyles: {
            0: { cellWidth: 65 },
            1: { cellWidth: 70 },
            2: { cellWidth: 70 },
            3: { cellWidth: 60 },
          },
          margin: { left: 14, right: 14 },
        });

        startY = doc.lastAutoTable.finalY + 12;

        // Add page if running low on space
        if (startY > 170) {
          doc.addPage();
          startY = 18;
        }
      });

    doc.save("development-plan.pdf");
  }, [plan, selectedValueData, selectedCompetencyIndexes, entriesByCompetency]);

  const isBusy = isCreating || isUpdating || isSelectingCompetencies;
  const isPageLoading = isLoading || isLoadingValues;

  const toggleStep = (step) => {
    setExpandedStep(expandedStep === step ? null : step);
  };

  return (
    <div className="learning-container">
      <div className="page-header">
        <h1 className="username">Development Plans</h1>
      </div>

      {isPageLoading && (
        <div className="development-loading">Loading your development plan...</div>
      )}

      {error && error.status !== 404 && (
        <div className="development-error">Failed to load development plan. Please try again later.</div>
      )}

      {!isPageLoading && (
        <div className="steps-container">

          {/* ── Step 1: Focus Area ── */}
          <div className={`step-card ${expandedStep === 1 ? "expanded" : ""} ${step1Done ? "done" : ""}`}>
            <button className="step-header" onClick={() => toggleStep(1)} type="button">
              <span className="step-number">1</span>
              <span className="step-label">Focus Area</span>
              {step1Done && expandedStep !== 1 && (
                <span className="step-summary">{plan.selectedValue}</span>
              )}
              <svg className={`step-chevron ${expandedStep === 1 ? "rotated" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedStep === 1 && (
              <div className="step-body">
                <ValueSelector
                  value={plan?.selectedValue || pendingValue}
                  onChange={handleValueChange}
                  disabled={isBusy}
                  values={values}
                />
              </div>
            )}
          </div>

          {/* ── Step 2: Competencies ── */}
          <div className={`step-card ${expandedStep === 2 ? "expanded" : ""} ${step2Done ? "done" : ""} ${!step1Done ? "locked" : ""}`}>
            <button
              className="step-header"
              onClick={() => step1Done && toggleStep(2)}
              type="button"
              disabled={!step1Done}
            >
              <span className="step-number">2</span>
              <span className="step-label">Competencies</span>
              {step2Done && expandedStep !== 2 && (
                <span className="step-summary">
                  {selectedCompetencyIndexes.length} selected
                </span>
              )}
              {!step1Done && <span className="step-locked-hint">Select a focus area first</span>}
              <svg className={`step-chevron ${expandedStep === 2 ? "rotated" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedStep === 2 && step1Done && selectedValueData && (
              <div className="step-body">
                <CompetencySelector
                  competencies={selectedValueData.competencies || []}
                  selectedIndexes={selectedCompetencyIndexes}
                  onSelectionChange={handleCompetencySelectionChange}
                  disabled={isBusy}
                />
                {selectedCompetencyIndexes.length > 0 && (
                  <button
                    className="step-continue-btn"
                    onClick={() => setExpandedStep(3)}
                    type="button"
                  >
                    Continue to Plans →
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── Step 3: Development Plans ── */}
          <div className={`step-card ${expandedStep === 3 ? "expanded" : ""} ${!step2Done ? "locked" : ""}`}>
            <button
              className="step-header"
              onClick={() => step2Done && toggleStep(3)}
              type="button"
              disabled={!step2Done}
            >
              <span className="step-number">3</span>
              <span className="step-label">Development Plans</span>
              {!step2Done && <span className="step-locked-hint">Select competencies first</span>}
              {step2Done && expandedStep === 3 && (
                <span
                  className="step-download-btn"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); handleDownloadPdf(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDownloadPdf(); } }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download PDF
                </span>
              )}
              <svg className={`step-chevron ${expandedStep === 3 ? "rotated" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedStep === 3 && step2Done && (
              <div className="step-body">
                <div className="dev-accordion-container">
                  {selectedCompetencyIndexes
                    .slice()
                    .sort((a, b) => a - b)
                    .map((compIndex) => {
                      const goalEntries = entriesByCompetency[compIndex] || [];
                      const compData = selectedValueData?.competencies?.find(c => (c.index ?? 0) === compIndex);
                      const compName = compData?.name || goalEntries[0]?.competencyName || `Competency ${compIndex + 1}`;
                      const recommendations = recommendationsByCompetency[compIndex] || [];
                      const recommendedResources = resourcesByCompetency[compIndex] || [];

                      return (
                        <CompetencyTable
                          key={`${plan.selectedValue}-comp-${compIndex}`}
                          competencyName={compName}
                          competencyIndex={compIndex}
                          goalEntries={goalEntries}
                          recommendations={recommendations}
                          recommendedResources={recommendedResources}
                          onSave={handleEntrySave}
                          onAddEntry={handleAddEntry}
                          onRemoveEntry={handleRemoveEntry}
                          isSaving={isSavingEntry}
                        />
                      );
                    })}
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
